import {useCallback, useEffect, useRef, useState} from 'react';
import {
    JobExecutionContext, JobStatus, MappingJob, MappingJobSummary, StartMappingJobRequest, UseMappingJobReturn
} from './descriptor-apply-types';
import {toast} from 'react-toastify';
import occurrenceReportDescriptorService from '../../../../../services/occurrence-report-descriptor.service';

/**
 * Format seconds as human-readable string (e.g., "~7 minutes", "~2 hours")
 */
export function formatSecondsRemaining(seconds: number | null | undefined): string {
    if (seconds === null || seconds === undefined || seconds <= 0) {
        return '';
    }

    if (seconds < 60) {
        return `~${Math.ceil(seconds)} seconds`;
    } else if (seconds < 3600) {
        const minutes = Math.ceil(seconds / 60);
        return `~${minutes} minute${minutes !== 1 ? 's' : ''}`;
    } else {
        const hours = Math.round(seconds / 3600 * 10) / 10;
        return `~${hours} hour${hours !== 1 ? 's' : ''}`;
    }
}

/**
 * Format processing rate as "X.X reports/sec"
 */
export function formatProcessingRate(rate: number | null | undefined): string {
    if (rate === null || rate === undefined || rate <= 0) {
        return '';
    }

    if (rate >= 1) {
        return `~${rate.toFixed(1)} reports/sec`;
    } else {
        // For very slow rates, show per minute
        const perMinute = rate * 60;
        return `~${perMinute.toFixed(1)} reports/min`;
    }
}

/**
 * Custom hook for managing descriptor mapping jobs
 * Handles job lifecycle: creation, status polling, cancellation
 */
export function useMappingJob(descriptorId: number): UseMappingJobReturn {
    const [currentJob, setCurrentJob] = useState<MappingJob | null>(null);
    const [existingJob, setExistingJob] = useState<JobExecutionContext | null>(null);
    const [isLoading, setIsLoading] = useState(false);
    const [error, setError] = useState<string | null>(null);
    // useRef instead of useState so that clearInterval always uses the current ID,
    // even inside stale useCallback closures captured by setInterval.
    const pollingIntervalRef = useRef<number | null>(null);
    // Always points to the latest checkStatus so the setInterval closure never goes stale.
    const checkStatusRef = useRef<(jobId: string) => Promise<void>>(async () => { /* initialised below */ });
    
    // Storage key for persisting job state
    const storageKey = `descriptor_mapping_job_${descriptorId}`;
    
    /**
     * Map backend state to frontend JobStatus
     */
    const mapBackendStateToJobStatus = (state: string): JobStatus => {
        switch (state) {
            case 'PENDING': return 'PENDING';
            case 'RUNNING': return 'RUNNING';
            case 'COMPLETED': return 'COMPLETED';
            case 'FAILED': return 'FAILED';
            case 'CANCELLED': return 'CANCELLED';
            default: return 'PENDING';
        }
    };
    
    // Load persisted job on mount
    useEffect(() => {
        const loadPersistedJob = async (): Promise<void> => {
            try {
                // Check localStorage first (for prototype)
                const storedJob = localStorage.getItem(storageKey);
                if (storedJob) {
                    const job = JSON.parse(storedJob) as MappingJob;
                    
                    // Only restore if job is not too old (24 hours)
                    const jobAge = Date.now() - new Date(job.startedAt).getTime();
                    const maxAge = 24 * 60 * 60 * 1000; // 24 hours
                    
                    if (jobAge < maxAge) {
                        setCurrentJob(job);

                        // Restart polling so the user sees live progress after returning to the tab
                        if (['PENDING', 'RUNNING'].includes(job.status)) {
                            startStatusPolling(parseInt(job.jobId));
                        }
                    } else {
                        // Clean up old job
                        localStorage.removeItem(storageKey);
                    }
                }
            } catch (err) {

                // eslint-disable-next-line no-console
                console.error('Failed to load persisted job:', err);
            }
        };
        
        loadPersistedJob();
    }, [descriptorId, storageKey]);
    
    // Clean up polling on unmount
    useEffect(() => {
        return () => {
            if (pollingIntervalRef.current) {
                clearInterval(pollingIntervalRef.current);
                pollingIntervalRef.current = null;
            }
        };
    }, []);
    
    // Start polling when existingJob is set and is active
    useEffect(() => {
        if (existingJob && ['PENDING', 'RUNNING'].includes(existingJob.status?.state) && !currentJob) {
            // Convert to MappingJob and set as current
            const mappingJob: MappingJob = {
                jobId: existingJob.executionId.toString(),
                descriptorId: descriptorId,
                status: mapBackendStateToJobStatus(existingJob.status?.state),
                source: existingJob.request?.parameters?.sourceNames?.[0] || 'Unknown',
                startedAt: existingJob.status?.submittedAt || new Date().toISOString(),
                progress: existingJob.status?.percentComplete || 0
            };

            setCurrentJob(mappingJob);
            localStorage.setItem(storageKey, JSON.stringify(mappingJob));

            // Start polling for this job
            startStatusPolling(existingJob.executionId);
        }
    // eslint-disable-next-line
    }, [existingJob?.executionId, existingJob?.status?.state]); // Only depend on specific fields to avoid loops
    
    /**
     * Check for existing mapping job
     */
    const checkExistingJob = useCallback(async (sources: string[]): Promise<void> => {
        if (sources.length === 0) {
            setExistingJob(null);
            return;
        }
        
        try {
            const job = await occurrenceReportDescriptorService.checkExistingMappingJob(descriptorId, sources);
            setExistingJob(job);
        } catch (err) {
            // If error is not 404, log it
            // eslint-disable-next-line no-console
            console.error('Error checking existing job:', err);
            setExistingJob(null);
        }
    }, [descriptorId]);

    /**
     * Start a new mapping job
     */
    const startJob = useCallback(async (request: StartMappingJobRequest): Promise<void> => {
        setIsLoading(true);
        setError(null);
        
        try {
            // Submit bulk mapping job to backend
            const response = await occurrenceReportDescriptorService.submitBulkDescriptorMapping(
                descriptorId,
                {
                    sources: [request.source],
                    confidenceThreshold: request.options?.confidenceThreshold || 0.7
                }
            );
            
            // Convert backend response to MappingJob format
            const newJob: MappingJob = {
                jobId: response.executionId.toString(),
                descriptorId: request.descriptorId,
                status: mapBackendStateToJobStatus(response.status?.state),
                source: request.source,
                startedAt: response.status?.submittedAt || new Date().toISOString(),
                progress: response.status?.percentComplete || 0
            };
            
            setCurrentJob(newJob);
            setExistingJob(response);
            localStorage.setItem(storageKey, JSON.stringify(newJob));
            
            // Start polling for status updates
            startStatusPolling(response.executionId);
            
            toast.success('Mapping job started successfully');
        } catch (err: any) {
            const errorMessage = err.message || 'Failed to start mapping job';

            // If the backend reports that a job with this correlation ID is already running
            // (possible race: concurrent submission or stale frontend state), silently
            // attach to the existing job rather than surfacing a confusing error.
            if (errorMessage.toLowerCase().includes('already exists') ||
                errorMessage.toLowerCase().includes('already active')) {
                try {
                    const running = await occurrenceReportDescriptorService
                        .checkExistingMappingJob(descriptorId, [request.source]);
                    if (running) {
                        setExistingJob(running);
                        return; // existingJob useEffect will create currentJob and start polling
                    }
                } catch {
                    // Fall through to show original error
                }
            }

            setError(errorMessage);
            toast.error(errorMessage);
        } finally {
            setIsLoading(false);
        }
    }, [descriptorId, storageKey]);

    /**
     * Stop polling for job status
     */
    const stopPolling = (): void => {
        if (pollingIntervalRef.current) {
            clearInterval(pollingIntervalRef.current);
            pollingIntervalRef.current = null;
        }
    };

    /**
     * Start polling for job status
     */
    const startStatusPolling = (executionId: number): void => {
        stopPolling();

        pollingIntervalRef.current = window.setInterval(async () => {
            await checkStatusRef.current(executionId.toString());
        }, 5000); // Poll every 5 seconds
    };

    /**
     * Cancel an active job
     */
    const cancelJob = useCallback(async (jobId: string): Promise<void> => {
        setIsLoading(true);

        try {
            // Stop polling first to avoid a status update racing the cancellation
            stopPolling();

            // Send cancellation request to backend
            await occurrenceReportDescriptorService.cancelJobExecution(parseInt(jobId));

            if (currentJob && currentJob.jobId === jobId) {
                const cancelledJob: MappingJob = {
                    ...currentJob,
                    status: 'CANCELLED' as JobStatus,
                    completedAt: new Date().toISOString()
                };

                setCurrentJob(cancelledJob);
                localStorage.setItem(storageKey, JSON.stringify(cancelledJob));
            }

            toast.info('Mapping job cancelled');
        } catch (err: any) {
            toast.error('Failed to cancel job');
        } finally {
            setIsLoading(false);
        }
    }, [currentJob, storageKey]);
    
    /**
     * Check the status of a job
     */
    const checkStatus = useCallback(async (jobId: string): Promise<void> => {
        try {
            // Get job status from backend
            const response = await occurrenceReportDescriptorService.getJobStatus(parseInt(jobId));

            // Extract progress metrics from status
            const status = response.status;
            const itemsProcessed = status?.itemsProcessed ?? null;
            const totalItems = status?.totalItems ?? null;
            const processingRatePerSecond = status?.processingRatePerSecond ?? null;
            const estimatedSecondsRemaining = status?.estimatedSecondsRemaining ?? null;

            // Create or update job with backend status.
            // jobId is always taken from the parameter — never from currentJob — so a stale
            // closure can never overwrite the correct execution ID back into state.
            const updatedJob: MappingJob = currentJob ? {
                ...currentJob,
                jobId,
                status: mapBackendStateToJobStatus(response.status?.state || response.state),
                progress: response.status?.percentComplete || currentJob.progress || 0,
                completedAt: response.status?.completedAt,
                errorMessage: response.status?.errorMessage,
                // Progress tracking fields
                itemsProcessed: itemsProcessed ?? currentJob.itemsProcessed,
                totalItems: totalItems ?? currentJob.totalItems,
                processingRatePerSecond: processingRatePerSecond ?? currentJob.processingRatePerSecond,
                estimatedSecondsRemaining: estimatedSecondsRemaining ?? currentJob.estimatedSecondsRemaining
            } : {
                // Create new job from backend response if currentJob doesn't exist
                jobId: response.executionId.toString(),
                descriptorId: descriptorId,
                status: mapBackendStateToJobStatus(response.status?.state || response.state),
                source: response.request?.parameters?.sourceNames?.[0] || 'Unknown',
                startedAt: response.status?.submittedAt || new Date().toISOString(),
                progress: response.status?.percentComplete || 0,
                completedAt: response.status?.completedAt,
                errorMessage: response.status?.errorMessage,
                // Progress tracking fields
                itemsProcessed: itemsProcessed ?? undefined,
                totalItems: totalItems ?? undefined,
                processingRatePerSecond: processingRatePerSecond ?? undefined,
                estimatedSecondsRemaining: estimatedSecondsRemaining ?? undefined
            };

            // If job has report count in parameters, use it
            if (response.result?.details?.totalReportsProcessed) {
                updatedJob.reportCount = response.result.details.totalReportsProcessed;
            }
            if (response.result?.details?.successfulReports) {
                updatedJob.mappedCount = response.result.details.successfulReports;
            }
            if (response.result?.details?.failedReports) {
                updatedJob.errorCount = response.result.details.failedReports;
            }

            setCurrentJob(updatedJob);
            setExistingJob(response);
            localStorage.setItem(storageKey, JSON.stringify(updatedJob));

            // Handle terminal states - stop polling for any of them
            const terminalStates = ['COMPLETED', 'FAILED', 'CANCELLED'];
            if (terminalStates.includes(response.status?.state)) {
                stopPolling();
            }

            if (response.status?.state === 'COMPLETED') {
                // Try to get job results
                try {
                    const results = await occurrenceReportDescriptorService.getJobResults(parseInt(jobId));
                    if (results?.details) {
                        updatedJob.mappedCount = results.details.successfulReports;
                        updatedJob.errorCount = results.details.failedReports;
                        setCurrentJob(updatedJob);
                    }
                } catch (err) {
                    // eslint-disable-next-line no-console
                    console.error('Failed to get job results:', err);
                }

                toast.success(
                    `Mapping completed! ${updatedJob.mappedCount || 'All'} reports successfully mapped.`
                );
            } else if (response.status?.state === 'FAILED') {
                toast.error(`Mapping job failed: ${response.status.errorMessage || 'Unknown error'}`);
            }

        } catch (err) {
            const message = err instanceof Error ? err.message : String(err);
            // If the backend returns 404 (job metadata deleted or never existed), the stale
            // localStorage entry is the source of truth no longer. Clear it and stop polling
            // so the UI resets to the "no active job" state.
            if (message.toLowerCase().includes('not found')) {
                stopPolling();
                localStorage.removeItem(storageKey);
                setCurrentJob(null);
                setExistingJob(null);
                return;
            }
            // eslint-disable-next-line no-console
            console.error('Failed to check job status:', err);
        }
    }, [currentJob, storageKey, descriptorId]);

    // Keep ref in sync with the latest checkStatus on every render.
    // This is intentionally outside useEffect — we want it synchronous so the
    // very next interval tick sees the up-to-date closure with the latest currentJob.
    checkStatusRef.current = checkStatus;

    /**
     * Clear the current job
     */
    const clearJob = useCallback(() => {
        setCurrentJob(null);
        setExistingJob(null);
        setError(null);
        localStorage.removeItem(storageKey);
    }, [storageKey]);
    
    /**
     * Get summary statistics for a completed job
     */
    const getJobSummary = useCallback(async (jobId: string): Promise<MappingJobSummary> => {
        try {
            const results = await occurrenceReportDescriptorService.getJobResults(parseInt(jobId));
            
            if (results?.details?.statistics || results?.summary) {
                const processingTime = currentJob?.completedAt && currentJob?.startedAt
                    ? (new Date(currentJob.completedAt).getTime() - new Date(currentJob.startedAt).getTime()) / 1000
                    : 0;
                const stats = results.details?.statistics ?? {};

                return {
                    jobId: jobId,
                    totalReports: stats.totalReportsProcessed || 0,
                    successfulMappings: stats.successfulReports || 0,
                    partialMappings: 0,
                    failedMappings: stats.failedReports || 0,
                    skippedReports: 0,
                    averageConfidence: 0,
                    processingTimeSeconds: processingTime,
                    topExtractedTerms: []
                };
            }
        } catch (err) {
            // eslint-disable-next-line no-console
            console.error('Failed to get job summary:', err);
        }
        
        throw new Error('Job results not available');
    }, [currentJob]);
    
    /**
     * Delete existing bulk mapping
     */
    const deleteMapping = useCallback(async (sources: string[]): Promise<void> => {
        setIsLoading(true);
        setError(null);
        
        try {
            await occurrenceReportDescriptorService.deleteBulkDescriptorMapping(descriptorId, sources);
            
            // Clear the existing job after successful deletion
            setExistingJob(null);
            setCurrentJob(null);
            
            toast.success('Mapping deleted successfully');
        } catch (err: any) {
            const errorMessage = err.message || 'Failed to delete mapping';
            setError(errorMessage);
            toast.error(errorMessage);
        } finally {
            setIsLoading(false);
        }
    }, [descriptorId]);

    return {
        currentJob,
        existingJob,
        isLoading,
        error,
        startJob,
        cancelJob,
        checkStatus,
        clearJob,
        getJobSummary,
        checkExistingJob,
        deleteMapping
    };
}